<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';
import ProjectRolesCell from './ProjectRolesCell.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

// Fixed agent-role vocabulary; labels are localized, values match the backend enum.
const ROLE_VALUES = ['project_manager', 'contributor', 'reviewer', 'observer'];
const roleOptions = computed(() =>
  ROLE_VALUES.map(value => ({
    value,
    label: t(`PROJECTS.TEAM.ROLE.${value.toUpperCase()}`),
  }))
);

const members = computed(() =>
  store.getters['projectMembers/getMembersByProject'](Number(props.projectId))
);
const uiFlags = computed(() => store.getters['projectMembers/getUIFlags']);

const agents = computed(() => store.getters['agents/getAgents'] || []);

const pickerOpen = ref(false);
const search = ref('');

// Agents not already on the team, optionally filtered by the search term.
const availableAgents = computed(() => {
  const memberIds = new Set(members.value.map(member => member.user_id));
  const term = search.value.trim().toLowerCase();
  return agents.value
    .filter(agent => !memberIds.has(agent.id))
    .filter(agent => !term || (agent.name || '').toLowerCase().includes(term));
});

const loadMembers = () =>
  store.dispatch('projectMembers/get', { projectId: props.projectId });

const addMember = async agent => {
  try {
    await store.dispatch('projectMembers/create', {
      projectId: props.projectId,
      userId: agent.id,
      roles: [],
    });
    search.value = '';
    pickerOpen.value = false;
  } catch (error) {
    useAlert(t('PROJECTS.TEAM.ERROR'));
  }
};

const updateRoles = async (member, roles) => {
  try {
    await store.dispatch('projectMembers/update', {
      projectId: props.projectId,
      id: member.id,
      roles,
    });
  } catch (error) {
    useAlert(t('PROJECTS.TEAM.ERROR'));
  }
};

const removeMember = async member => {
  try {
    await store.dispatch('projectMembers/delete', {
      projectId: props.projectId,
      id: member.id,
    });
  } catch (error) {
    useAlert(t('PROJECTS.TEAM.ERROR'));
  }
};

watch(() => props.projectId, loadMembers);
onMounted(() => {
  store.dispatch('agents/get');
  loadMembers();
});
</script>

<template>
  <section class="flex flex-col rounded-lg border border-n-weak bg-n-solid-1">
    <!-- Card header: title + count + add agent -->
    <header
      class="flex items-center gap-2 px-4 h-12 border-b border-n-weak shrink-0"
    >
      <span class="i-lucide-users size-4 text-n-slate-11" />
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('PROJECTS.PEOPLE.TEAM_TITLE') }}
      </h3>
      <span
        class="px-1.5 py-0.5 text-xs rounded-md tabular-nums bg-n-alpha-2 text-n-slate-11"
      >
        {{ members.length }}
      </span>
      <div class="relative ml-auto">
        <OnClickOutside @trigger="pickerOpen = false">
          <Button
            :label="t('PROJECTS.TEAM.ADD')"
            icon="i-lucide-user-plus"
            size="sm"
            color="blue"
            @click="pickerOpen = !pickerOpen"
          />
          <div
            v-if="pickerOpen"
            class="absolute right-0 z-20 mt-1 w-72 overflow-hidden border rounded-md shadow-lg border-n-weak bg-n-solid-2"
          >
            <input
              v-model="search"
              class="w-full h-8 px-2.5 text-sm border-b reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
              :placeholder="t('PROJECTS.TEAM.SEARCH_PLACEHOLDER')"
            />
            <ul class="overflow-y-auto max-h-64 divide-y divide-n-weak/60">
              <li
                v-for="agent in availableAgents"
                :key="agent.id"
                class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-n-alpha-1"
                @click="addMember(agent)"
              >
                <Avatar :name="agent.name" :src="agent.thumbnail" :size="22" />
                <span class="font-medium truncate text-n-slate-12">
                  {{ agent.name }}
                </span>
              </li>
              <li
                v-if="!availableAgents.length"
                class="px-3 py-2 text-xs text-n-slate-10"
              >
                {{ t('PROJECTS.TEAM.NO_AGENTS') }}
              </li>
            </ul>
          </div>
        </OnClickOutside>
      </div>
    </header>

    <!-- Card body: members list -->
    <div class="p-4">
      <ProjectEmptyState
        v-if="!members.length && !uiFlags.isFetching"
        icon="i-lucide-users"
        :title="t('PROJECTS.TEAM.EMPTY')"
      />
      <table v-else class="w-full text-sm border-collapse">
        <thead>
          <tr
            class="text-xs text-left uppercase text-n-slate-10 [&>th]:font-medium [&>th]:pb-2 [&>th]:pr-3"
          >
            <th>{{ t('PROJECTS.TEAM.COL_AGENT') }}</th>
            <th>{{ t('PROJECTS.TEAM.COL_ROLES') }}</th>
            <th class="w-8" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="member in members"
            :key="member.id"
            class="group transition-colors [&>td]:border-t [&>td]:border-n-weak/60 [&>td]:py-1.5 [&>td]:pr-3 [&>td]:align-middle hover:bg-n-alpha-1"
          >
            <td class="min-w-0">
              <span class="flex items-center min-w-0 gap-2">
                <Avatar
                  :name="member.user?.name"
                  :src="member.user?.thumbnail"
                  :size="24"
                />
                <span class="font-medium truncate text-n-slate-12">
                  {{ member.user?.name }}
                </span>
              </span>
            </td>
            <td>
              <ProjectRolesCell
                :model-value="member.roles || []"
                :options="roleOptions"
                @update:model-value="updateRoles(member, $event)"
              />
            </td>
            <td class="text-right">
              <Button
                v-tooltip.top="t('PROJECTS.TEAM.REMOVE')"
                variant="ghost"
                color="ruby"
                size="xs"
                icon="i-lucide-x"
                class="transition-opacity opacity-0 group-hover:opacity-100"
                @click="removeMember(member)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
